// src/components/FeedbackTab.jsx
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { usePostHog } from 'posthog-js/react';
import { captureDomScreenshot } from '../utils/captureDomScreenshot';

export default function FeedbackTab() {
  const posthog = usePostHog();
  const [isReady, setIsReady] = useState(false);
  const [isOpen, setIsOpen] = useState(false);
  const [comment, setComment] = useState('');
  const [screenshots, setScreenshots] = useState([]);
  const [isCapturing, setIsCapturing] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(false);
  const closeTimeoutRef = useRef();
  const MAX_SCREENSHOTS = 4;

  useEffect(() => {
    if (typeof document === 'undefined') {
      return;
    }

    // ensure the container exists (one per app)
    let el = document.getElementById('feedback-tab-root');
    if (!el) {
      el = document.createElement('div');
      el.id = 'feedback-tab-root';
      document.body.appendChild(el);
    }
    setIsReady(true);

    return () => {
      if (closeTimeoutRef.current) {
        clearTimeout(closeTimeoutRef.current);
      }
    };
  }, []);

  useEffect(() => {
    if (!isOpen) {
      return;
    }
    const handleKeyDown = (event) => {
      if (event.key === 'Escape') {
        setIsOpen(false);
      }
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [isOpen]);

  const resetForm = useCallback(() => {
    setComment('');
    setScreenshots([]);
    setError(null);
  }, []);

  const handleOpen = useCallback(() => {
    setSuccess(false);
    setIsOpen(true);
    posthog?.capture('feedback_widget_opened');
  }, [posthog]);

  const handleClose = useCallback(() => {
    setIsOpen(false);
    resetForm();
  }, [resetForm]);

  const handleRemoveScreenshot = useCallback((id) => {
    setScreenshots((prev) => prev.filter((shot) => shot.id !== id));
  }, []);

  const handleCaptureScreenshot = useCallback(async () => {
    if (isCapturing) {
      return;
    }
    if (screenshots.length >= MAX_SCREENSHOTS) {
      setError(`You can add up to ${MAX_SCREENSHOTS} screenshots per submission.`);
      return;
    }
    setError(null);
    setIsCapturing(true);
    const root = document.getElementById('feedback-tab-root');
    const originalVisibility = root?.style.visibility;
    if (root) {
      root.style.visibility = 'hidden';
    }
    try {
      await document.fonts?.ready?.catch(() => {});
      const target = document.body;
      const { dataUrl, width, height } = await captureDomScreenshot(target, {
        pixelRatio: Math.min(window.devicePixelRatio || 1, 3),
      });
      setScreenshots((prev) => {
        const shot = {
          id: `${Date.now()}-${Math.random().toString(16).slice(2)}`,
          dataUrl,
          width,
          height
        };
        const next = [...prev, shot];
        posthog?.capture('feedback_screenshot_captured', {
          total: next.length,
          width: shot.width,
          height: shot.height
        });
        return next;
      });
    } catch (err) {
      console.error('Failed to capture screenshot', err);
      setError('We could not capture a screenshot. Try again.');
    } finally {
      if (root) {
        root.style.visibility = originalVisibility ?? '';
      }
      setIsCapturing(false);
    }
  }, [MAX_SCREENSHOTS, isCapturing, posthog, screenshots.length]);

  const uploadScreenshots = useCallback(async () => {
    if (screenshots.length === 0) {
      return [];
    }
    const payload = {
      images: screenshots.map((shot) => shot.dataUrl),
    };
    const response = await fetch('/api/feedback/screenshots', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      credentials: 'include',
      body: JSON.stringify(payload),
    });
    if (!response.ok) {
      let message = 'Failed to upload screenshots.';
      try {
        const result = await response.json();
        if (result?.message) {
          message = result.message;
        }
      } catch (err) {
        // ignore JSON parse errors
      }
      throw new Error(message);
    }
    const result = await response.json().catch(() => ({}));
    const uploaded = Array.isArray(result?.screenshots) ? result.screenshots : [];
    return uploaded;
  }, [screenshots]);

  const canSubmit = useMemo(() => {
    return comment.trim().length > 0 || screenshots.length > 0;
  }, [comment, screenshots.length]);

  const handleSubmit = useCallback(
    async (event) => {
      event.preventDefault();
      if (isSubmitting) {
        return;
      }
      if (!canSubmit) {
        setError('Add a comment or at least one screenshot before sending.');
        return;
      }
      setError(null);
      setIsSubmitting(true);
      try {
        const uploadedScreenshots = await uploadScreenshots();
        posthog?.capture('feedback_submitted', {
          comment: comment.trim(),
          screenshot_count: uploadedScreenshots.length,
          screenshots: uploadedScreenshots.map((item) => item.url),
          screenshot_meta: uploadedScreenshots.map((item) => ({
            id: item.id,
            content_type: item.contentType,
            size: item.size,
            signed: item.signed ?? true
          }))
        });
        setSuccess(true);
        resetForm();
        closeTimeoutRef.current = window.setTimeout(() => {
          setIsOpen(false);
          setSuccess(false);
          closeTimeoutRef.current = undefined;
        }, 1600);
      } catch (err) {
        console.error('Failed to submit feedback', err);
        setError(err?.message ?? 'Something went wrong sending your feedback. Please try again.');
        posthog?.capture('feedback_submission_failed', {
          reason: err?.message ?? 'unknown_error',
          has_comment: Boolean(comment.trim()),
          screenshot_count: screenshots.length
        });
      } finally {
        setIsSubmitting(false);
      }
    },
    [canSubmit, comment, isSubmitting, posthog, resetForm, screenshots.length, uploadScreenshots]
  );

  if (!isReady || typeof document === 'undefined') return null;

  const root = document.getElementById('feedback-tab-root');
  if (!root) return null;

  const node = (
    <>
      <button
        type="button"
        aria-label="Send feedback"
        className="fixed bottom-36 right-0 z-[2147483001] origin-bottom-right -rotate-90 rounded-t-lg border border-default-200 bg-content2 px-3 py-1.5 text-[13px] font-medium text-foreground shadow-medium transition hover:border-default-300 hover:bg-content3"
        onClick={handleOpen}
      >
        ✨ Feedback
      </button>
      {isOpen && (
        <div
          role="dialog"
          aria-modal="true"
          className="fixed inset-0 z-[2147483000] flex items-end justify-end bg-black/40 p-5 backdrop-blur-sm"
        >
          <div className="w-[min(480px,calc(100%-32px))] space-y-4 rounded-2xl border border-default-200 bg-content1 px-6 py-6 text-foreground shadow-large">
            <div className="flex items-start justify-between gap-3">
              <div>
                <h2 className="text-lg font-semibold text-foreground">Share your feedback</h2>
                <p className="mt-1 text-sm text-default-500">
                  Tell us what happened and attach one or more screenshots so we can help.
                </p>
              </div>
              <button
                type="button"
                onClick={handleClose}
                aria-label="Close feedback form"
                className="text-xl text-default-500 transition hover:text-foreground"
              >
                ×
              </button>
            </div>

            <form onSubmit={handleSubmit} className="flex flex-col gap-4">
              <label className="flex flex-col gap-2">
                <span className="text-sm font-medium text-foreground">What happened?</span>
                <textarea
                  value={comment}
                  onChange={(event) => setComment(event.target.value)}
                  placeholder="Share any details that will help us reproduce the issue…"
                  rows={4}
                  className="min-h-[120px] w-full resize-y rounded-xl border border-default-200 bg-content2 px-4 py-3 text-sm text-foreground placeholder:text-default-400 focus:border-primary focus:outline-none focus:ring-2 focus:ring-primary/30"
                />
              </label>

              <div>
                <div className="mb-2 flex items-center justify-between">
                  <span className="text-sm font-medium text-foreground">Screenshots</span>
                  <button
                    type="button"
                    onClick={handleCaptureScreenshot}
                    disabled={isCapturing}
                    className="inline-flex items-center gap-2 rounded-full border border-default-200 bg-content2 px-3 py-1.5 text-xs font-semibold tracking-wide text-foreground transition hover:border-default-300 hover:bg-content3 disabled:cursor-progress disabled:opacity-60"
                  >
                    {isCapturing ? 'Capturing…' : 'Capture this screen'}
                  </button>
                </div>

                {screenshots.length > 0 && (
                  <div className="grid grid-cols-[repeat(auto-fill,minmax(120px,1fr))] gap-3">
                    {screenshots.map((shot, index) => (
                      <div key={shot.id} className="relative overflow-hidden rounded-xl border border-default-200 bg-content2">
                        <img src={shot.dataUrl} alt={`Screenshot ${index + 1}`} className="block w-full" />
                        <button
                          type="button"
                          onClick={() => handleRemoveScreenshot(shot.id)}
                          aria-label="Remove screenshot"
                          className="absolute right-1.5 top-1.5 inline-flex h-6 w-6 items-center justify-center rounded-full bg-black/60 text-sm text-background transition hover:bg-black/80"
                        >
                          ×
                        </button>
                      </div>
                    ))}
                  </div>
                )}
                <p className="mt-2 text-xs text-default-500">
                  Screenshots upload securely with your feedback. Limit {MAX_SCREENSHOTS}.
                </p>
              </div>

              {error && (
                <div role="alert" className="rounded-xl border border-red-300 bg-red-50 px-3 py-2 text-sm text-red-800 dark:border-red-400/50 dark:bg-red-500/20 dark:text-red-100">
                  {error}
                </div>
              )}

              {success && (
                <div role="status" className="rounded-xl border border-emerald-300 bg-emerald-50 px-3 py-2 text-sm text-emerald-800 dark:border-emerald-400/50 dark:bg-emerald-500/20 dark:text-emerald-100">
                  Thanks! Your feedback is on its way.
                </div>
              )}

              <button
                type="submit"
                disabled={isSubmitting || !canSubmit}
                className="self-end rounded-full bg-primary px-4 py-2 text-sm font-semibold text-primary-foreground shadow-lg transition hover:shadow-xl disabled:cursor-not-allowed disabled:opacity-60"
              >
                {isSubmitting ? 'Sending…' : 'Send feedback'}
              </button>
            </form>
          </div>
        </div>
      )}
    </>
  );

  return createPortal(node, root);
}
